import { useFormik } from 'formik';
import { FormikErrors } from 'formik/dist/types';
import { useEffect } from 'react';
import * as yup from 'yup';
import {
  AnyObject,
  AnySchema,
  ArraySchema,
  BooleanSchema,
  StringSchema,
} from 'yup';

import { FormStatus } from '~types/Forms';

export type UseFormFieldValidate =
  | StringSchema
  | BooleanSchema
  | ArraySchema<AnySchema[] | undefined, AnyObject>;

export type UseFormField = {
  validate?: UseFormFieldValidate;
  value?: any;
};

type Values<Fields> = Record<keyof Fields, UseFormField['value']>;

type Props<Fields> = {
  fields: Fields;
  submit(values: Values<Fields>): void;
  onError?(status: FormStatus): void;
  onSubmit?(status: FormStatus): void;
  onSuccess?(status: FormStatus): void;
  validate?: (
    values: Values<Fields>,
  ) => void | object | Promise<FormikErrors<Fields>>;
};

export function useForm<Fields extends Record<string, UseFormField>>({
  fields,
  submit,
  validate,
  onSubmit = (status) => status,
  onError = (status) => status,
  onSuccess = (status) => status,
}: Props<Fields>) {
  const formikProps = Object.entries(fields).reduce(
    (result, [name, field]) => {
      const fieldName: keyof Fields = name;

      result.values[fieldName] = field.value;

      if (field.validate) {
        result.validation[fieldName] = field.validate;
      }

      return result;
    },
    {
      values: {} as Values<Fields>,
      validation: {} as Record<keyof Fields, UseFormFieldValidate>,
    },
  );
  const formik = useFormik({
    validate,
    initialStatus: {
      isSubmitting: false,
    },
    initialValues: formikProps.values,
    validationSchema: yup.object(formikProps.validation),
    onSubmit(values, actions) {
      actions.setStatus({
        isSubmitting: true,
      });

      return submit(values);
    },
  });

  useEffect(() => {
    const status = formik.status as FormStatus;

    if (status.isSubmitting) {
      onSubmit(status);
    }

    if (status.error) {
      onError(status);
    }

    if (status.success) {
      onSuccess(status);
    }
  }, [formik.status, onSuccess, onError, onSubmit]);

  return {
    ...formik,
    status: formik.status as FormStatus,
    setStatus(status: FormStatus) {
      formik.setStatus(status);
    },
  };
}
